/**
 * A note from its plaintext to the link, and from the link back to its blocks. What a note holds,
 * its blocks and their padding, is `@skysend/note-format`. The caller hands in the plaintext,
 * since a client that talks to a server from before v3 sends a legacy note instead.
 */
import { computeAuthToken, computeOwnerToken, decryptNoteContent, deriveKeys, encryptNoteContent, generateSalt, generateSecret, toBase64url, } from "@skysend/crypto";
import { readNote } from "@skysend/note-format";
import { fromBase64, keysFromLink } from "./download.js";
import { protectSecret, toBase64 } from "./upload.js";
/**
 * Encrypts a note and creates it on the server. The server gets the ciphertext, the salt, the
 * two tokens and the content type, which for a note of blocks says only that it is made of blocks.
 */
export async function createEncryptedNote(options) {
    const secret = generateSecret();
    const salt = generateSalt();
    const keys = await deriveKeys(secret, salt);
    const encrypted = await encryptNoteContent(options.plaintext, keys.metaKey);
    const { effectiveSecret, passwordSalt, passwordAlgo } = await protectSecret(secret, options.password, options.argon2id);
    const ownerToken = toBase64url(await computeOwnerToken(effectiveSecret, salt));
    const hasPassword = passwordSalt !== undefined;
    const { id } = await options.api.createNote({
        encryptedContent: toBase64(encrypted.ciphertext),
        nonce: toBase64(encrypted.nonce),
        salt: toBase64url(salt),
        ownerToken,
        authToken: toBase64url(await computeAuthToken(keys.authKey)),
        contentType: options.contentType,
        maxViews: options.maxViews,
        expireSec: options.expireSec,
        hasPassword,
        ...(passwordSalt ? { passwordSalt: toBase64url(passwordSalt), passwordAlgo } : {}),
    });
    return { id, ownerToken, effectiveSecret: toBase64url(effectiveSecret), hasPassword };
}
/**
 * Opens a note from its link. The password of a protected note is checked first, so a wrong one
 * uses up no view, and the answer is then null. Otherwise one view is used up, and with a limit
 * of one the note is gone, so a note that does not parse comes back as plain text instead of
 * being lost. Notes from before v3 read through the legacy readers of `readNote`.
 */
export async function openNote(options) {
    const { api, id, info } = options;
    const { keys, authToken } = await keysFromLink(options.secret, info.salt, info.hasPassword && options.password
        ? { password: options.password, saltB64: info.passwordSalt ?? "", argon2id: options.argon2id }
        : undefined);
    if (info.hasPassword && !(await api.verifyNotePassword(id, authToken)))
        return null;
    const view = await api.viewNote(id, authToken);
    const plaintext = await decryptNoteContent(fromBase64(view.encryptedContent), fromBase64(view.nonce), keys.metaKey);
    let blocks;
    let unreadable = false;
    try {
        blocks = readNote(info.contentType, plaintext);
    }
    catch {
        blocks = [{ type: "text", format: "plain", text: plaintext }];
        unreadable = true;
    }
    return { blocks, unreadable, viewCount: view.viewCount, maxViews: view.maxViews };
}
//# sourceMappingURL=note.js.map